import { authExchange } from "@urql/exchange-auth";
import { retryExchange } from "@urql/exchange-retry";
import { cacheExchange, createClient, fetchExchange } from "urql";
import { firebaseAuth } from "../lib/firebase";
import { sentryExchange } from "./sentryExchange";

const BFF_URL = import.meta.env.VITE_BFF_URL || "http://localhost:4000";

async function fetchFirebaseIdToken(): Promise<string | null> {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) return null;
  return currentUser.getIdToken();
}

function createPublicExchanges() {
  return [
    cacheExchange,
    ...(!import.meta.env.DEV ? [sentryExchange] : []),
    // 候補者向け公開エンドポイントは unique_token を JWT 同等に扱う:
    // 各 operation の context.candidateToken を Authorization ヘッダに載せる。
    // 個別 operation で token を渡さなければ送らない（無効リクエストは backend 401）。
    authExchange(async (utils) => ({
      addAuthToOperation(operation) {
        const candidateToken = operation.context.candidateToken as
          | string
          | undefined;
        if (!candidateToken) return operation;
        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${candidateToken}`,
        });
      },
      didAuthError(error) {
        return error.response?.status === 401;
      },
      willAuthError() {
        return false;
      },
      async refreshAuth() {},
    })),
    retryExchange({
      initialDelayMs: 1000,
      maxDelayMs: 5000,
      randomDelay: true,
      maxNumberAttempts: 3,
      // mutation のリトライは副作用が重複するため厳禁。bookInterview の再送で二重予約になりうる
      retryIf: (error, op) => !!error.networkError && op.kind === "query",
    }),
    fetchExchange,
  ];
}

function createExchanges() {
  return [
    cacheExchange,
    ...(!import.meta.env.DEV ? [sentryExchange] : []),
    authExchange(async (utils) => {
      let currentToken: string | null = await fetchFirebaseIdToken();

      return {
        addAuthToOperation(operation) {
          if (currentToken) {
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${currentToken}`,
            });
          }
          return operation;
        },

        didAuthError(error) {
          // FORBIDDEN (認可エラー) は再ログインで解決しないため authError として扱わない。
          // 認可エラーは呼び出し元で個別にハンドリング (toast / バナー等) する想定。
          return (
            error.response?.status === 401 ||
            error.graphQLErrors?.some(
              (e) => e.extensions?.code === "UNAUTHENTICATED",
            )
          );
        },

        willAuthError() {
          return firebaseAuth.currentUser === null || currentToken === null;
        },

        async refreshAuth() {
          currentToken = await fetchFirebaseIdToken();
          if (currentToken === null) {
            window.location.href = "/employee_interview/login";
          }
        },
      };
    }),
    // Cold start時のネットワークエラーに対するリトライ。
    // mutation は副作用が重複するため Query 限定（apps/CLAUDE.md「書き込み系の自動リトライは禁止」）
    retryExchange({
      initialDelayMs: 1000,
      maxDelayMs: 5000,
      randomDelay: true,
      maxNumberAttempts: 3,
      retryIf: (error, op) => !!error.networkError && op.kind === "query",
    }),
    fetchExchange,
  ];
}

export const adminClient = createClient({
  url: `${BFF_URL}/employee_interview/graphql`,
  exchanges: createExchanges(),
});

export const selfClient = createClient({
  url: `${BFF_URL}/employee_interview_self/graphql`,
  exchanges: createExchanges(),
});

export const candidatePublicClient = createClient({
  url: `${BFF_URL}/employee_interview_candidate_public/graphql`,
  exchanges: createPublicExchanges(),
});
